/**
 * Quebec sales tax breakdown for display (TPS + TVQ).
 *
 * Application rules in this product (verify against your advisor / Revenu Québec):
 * - TPS (GST): 5% on the taxable base
 * - TVQ (QST): 9.975% on the same taxable base (not on the amount that includes TPS)
 *
 * For stays, callers pass `preTax + taxe d'hébergement` as the base so both taxes apply to
 * (prix + taxe d'hébergement); TPS is not included in the TVQ base.
 *
 * @see https://www.revenuquebec.ca/en/businesses/consumption-taxes/gsthst-and-qst/basic-rules-for-applying-the-gsthst-and-qst/tables-of-gst-and-qst-rates/
 */
export const QUEBEC_GST_RATE = 0.05;
export const QUEBEC_QST_RATE = 0.09975;
/** Taxe d'hébergement — fixed amount per night (not tied to nightly rate). */
export const TAXE_HEBERGEMENT_FIXED_PER_NIGHT = 3.5;

export function roundMoneyCAD(value: number): number {
  return Math.round((value + Number.EPSILON) * 100) / 100;
}

/** Total taxe d'hébergement for a stay when enabled (`$3.50 × nights`). */
export function taxeHebergementAmountForNights(nights: number, enabled: boolean): number {
  if (!enabled) return 0;
  const n = Math.max(0, nights);
  return roundMoneyCAD(n * TAXE_HEBERGEMENT_FIXED_PER_NIGHT);
}

export type QuebecTaxBreakdown = {
  /** Amount before TPS/TVQ (stored nightly rate in the app) */
  preTax: number;
  tps: number;
  tvq: number;
  /** preTax + tps + tvq */
  total: number;
};

/**
 * Computes TPS, TVQ, and total from the taxable base amount (before TPS/TVQ).
 *
 * **Règle A (sans taxe d'hébergement) :** sur un même `prix de base` :
 * - `TPS = arrondi(prix de base × 5 %)`
 * - `TVQ = arrondi(prix de base × 9,975 %)` — la TVQ ne porte pas sur la TPS
 * - `total = prix de base + TPS + TVQ`
 */
export function quebecTaxFromPreTax(preTaxAmount: number): QuebecTaxBreakdown {
  const preTax = Math.max(0, preTaxAmount);
  const tps = roundMoneyCAD(preTax * QUEBEC_GST_RATE);
  const tvq = roundMoneyCAD(preTax * QUEBEC_QST_RATE);
  const total = roundMoneyCAD(preTax + tps + tvq);
  return { preTax, tps, tvq, total };
}

export type QuebecStayNightPricing = {
  preTax: number;
  tps: number;
  tvq: number;
  /** Fixed lodging tax for one night when enabled ($3.50). */
  taxeHebergement: number;
  /** preTax + TPS + TVQ + taxeHebergement (TPS/TVQ each on preTax + taxeHebergement). */
  total: number;
};

/**
 * Per-night preview: optional fixed taxe d'hébergement ($3.50), then TPS and TVQ on (pre-tax + TH).
 */
export function quebecStayNightPricing(
  preTaxAmount: number,
  taxeHebergementEnabled: boolean,
): QuebecStayNightPricing {
  const preTax = Math.max(0, preTaxAmount);
  const taxeHebergement = taxeHebergementAmountForNights(1, taxeHebergementEnabled);
  const gstQstBase = preTax + taxeHebergement;
  const q = quebecTaxFromPreTax(gstQstBase);
  return {
    preTax,
    tps: q.tps,
    tvq: q.tvq,
    taxeHebergement,
    total: q.total,
  };
}

/** Pre-tax subtotal + nights → TPS/TVQ/TH/total (forfaits VUM, options multi-nuits, etc.). */
export function quebecStaySubtotalPricing(
  preTaxSubtotal: number,
  nights: number,
  taxeHebergementEnabled: boolean,
): QuebecStayNightPricing {
  const preTax = Math.max(0, preTaxSubtotal);
  const taxeHebergement = taxeHebergementAmountForNights(nights, taxeHebergementEnabled);
  const gstQstBase = preTax + taxeHebergement;
  const q = quebecTaxFromPreTax(gstQstBase);
  return {
    preTax,
    tps: q.tps,
    tvq: q.tvq,
    taxeHebergement,
    total: q.total,
  };
}

/** Guest-facing catalog price from stored pre-tax rate (TPS + TVQ). Use for activities, packages, etc. */
export function clientCatalogPriceActivity(preTaxAmount: number): number {
  return quebecTaxFromPreTax(preTaxAmount).total;
}

/** Guest-facing nightly rate from stored pre-tax price (TPS + TVQ + optional taxe d'hébergement). */
export function clientCatalogPriceStayPerNight(
  preTaxNight: number,
  taxeHebergementEnabled: boolean,
): number {
  return quebecStayNightPricing(preTaxNight, taxeHebergementEnabled).total;
}

/**
 * Tax-included total for an accommodation line (`preTaxPerNight` × `nights`).
 * Taxe d'hébergement = $3.50 × nights (when enabled), independent of nightly rate.
 */
export function clientCatalogStayLineTaxIncl(
  preTaxPerNight: number,
  nights: number,
  taxeHebergementEnabled: boolean,
): number {
  const n = Math.max(0, nights);
  if (n === 0) return 0;
  const preTaxTotal = roundMoneyCAD(preTaxPerNight * n);
  return quebecStaySubtotalPricing(preTaxTotal, n, taxeHebergementEnabled).total;
}

/** Tax-included total for activity / package pre-tax subtotals (TPS + TVQ only; no lodging tax). */
export function clientCatalogGoodsLineTaxIncl(preTaxSubtotal: number): number {
  return quebecTaxFromPreTax(Math.max(0, preTaxSubtotal)).total;
}

/**
 * Inverse of checkout when only tax-included total is known.
 * `lodgingNights` drives fixed TH ($3.50 × nights); `preTax` in the result is room subtotal before TH/TPS/TVQ.
 */
export function inferLinearCheckoutStyleBreakdownFromTotalIncl(
  totalCharged: number,
  taxeHebergementEnabled: boolean,
  lodgingNights = 1,
): { preTax: number; gst: number; qst: number; taxeHebergement: number } {
  const T = Math.max(0, totalCharged);
  if (T === 0) {
    return { preTax: 0, gst: 0, qst: 0, taxeHebergement: 0 };
  }
  const taxeHebergement = taxeHebergementAmountForNights(lodgingNights, taxeHebergementEnabled);
  let lo = 0;
  let hi = T;
  for (let i = 0; i < 64; i++) {
    const mid = roundMoneyCAD((lo + hi) / 2);
    const sum = quebecTaxFromPreTax(mid + taxeHebergement).total;
    if (sum <= T) lo = mid;
    else hi = mid;
  }
  const preTax = roundMoneyCAD((lo + hi) / 2);
  const { tps: gst, tvq: qst } = quebecTaxFromPreTax(preTax + taxeHebergement);
  return { preTax, gst, qst, taxeHebergement };
}
